// ./src/utils/api/RsvpQuestionService.ts
//
// Registration questions asked when guests respond or buy tickets. Mirrors the
// eventsmagic-api RsvpQuestion SDL. `where.id` accepts one id or many — a bulk
// update (>1 id) may only change status (server-enforced). Choice-type questions
// carry `options` (managed via RsvpQuestionOptionService).
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import type { AnswerSheetInput } from './answerSheetTypes';

const OPTION_FIELDS = `
  id
  rsvpQuestionId
  label
  optionValue
  description
  price
  isDefault
  orderNo
  status
`;

const FIELDS = `
  id
  eventId
  questionType
  askedOn
  partnerKinds
  valueType
  personField
  title
  helpText
  placeholder
  isRequired
  requiredScope
  askOncePerGroup
  conditionMatch
  orderNo
  settings
  status
  rights
  personField
  options { ${OPTION_FIELDS} }
  conditions {
    id
    conditionKind
    operator
    rsvpStatuses
    guestGroupId
  }
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query RsvpQuestionList($filter: RsvpQuestionFilter, $params: PaginationAndSorting) {
    rsvpQuestionList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// One question, as the server now holds it — with its options and conditions (#126: re-read after a save that half-failed).
const GET_QUERY = gql`
  query RsvpQuestionGet($id: ID) {
    rsvpQuestionGet(id: $id) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// The custom-question report (#42) — one server aggregate behind the whole page.
const STATS_QUERY = gql`
  query EventRsvpQuestionStats($params: EventRsvpQuestionStatsInput) {
    eventRsvpQuestionStats(params: $params) {
      code
      data {
        eventId
        audience
        includePersonFields
        audienceGuests
        audienceHeads
        respondedGuests
        totalAnswers
        questionCount
        hiddenPersonFieldQuestions
        questions {
          rsvpQuestionId
          title
          questionType
          valueType
          statKind
          personField
          orderNo
          status
          answerCount
          guestCount
          numberMin
          numberMax
          numberAvg
          numberSum
          yesCount
          noCount
          earliestDate
          latestDate
          options { rsvpQuestionOptionId label orderNo count }
          textAnswers { eventGuestId guestName value answeredAt }
          textTruncated
        }
      }
      errors { name errors warnings }
    }
  }
`;

// #236 — the guests' answers sheet: one row per guest with the questions the host picked.
const ANSWER_SHEET_QUERY = gql`
  query EventRsvpAnswerSheet($params: EventRsvpAnswerSheetInput) {
    eventRsvpAnswerSheet(params: $params) {
      code
      data {
        eventId
        includeContactQuestions
        onlyWithAnswers
        sort
        guestCount
        groupSetId
        groupSetName
        questions { rsvpQuestionId title questionType personField onlyAnswers }
        sections { guestGroupId name eventGuestIds }
        rows {
          eventGuestId
          name
          firstName
          lastName
          rsvpStatus
          partySize
          seatAssignment
          guestGroupIds
          answers { rsvpQuestionId value }
        }
      }
      errors { name errors warnings }
    }
  }
`;

// #236 — the same sheet as a CSV file, built on the server (owner, 2026-10-26).
const ANSWER_SHEET_CSV_QUERY = gql`
  query EventRsvpAnswerSheetCsv($params: EventRsvpAnswerSheetInput) {
    eventRsvpAnswerSheetCsv(params: $params) {
      code
      data { fileName content rowCount }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation RsvpQuestionCreate($params: RsvpQuestionInput) {
    rsvpQuestionCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation RsvpQuestionUpdate($where: RsvpQuestionWhereInput, $params: RsvpQuestionInput) {
    rsvpQuestionUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation RsvpQuestionRemove($where: RsvpQuestionWhereInput) {
    rsvpQuestionRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface RsvpQuestionOption {
  id?: string;
  rsvpQuestionId?: string | null;
  label?: string | null;
  optionValue?: string | null;
  description?: string | null;
  price?: number | null;
  isDefault?: boolean | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface RsvpQuestion {
  id?: string;
  eventId?: string | null;
  questionType?: string | null;
  /** rsvp | partner_application — which form this question is on. Create-only. */
  askedOn?: string | null;
  /** For a partner_application question: the kinds it is asked of. Null/empty = every kind. */
  partnerKinds?: string[] | null;
  valueType?: string | null;
  personField?: string | null;
  title?: string | null;
  helpText?: string | null;
  placeholder?: string | null;
  isRequired?: boolean | null;
  /** WHO must answer: none | all | primary | adults. The server derives isRequired from this. */
  requiredScope?: string | null;
  askOncePerGroup?: boolean | null;
  conditionMatch?: string | null;
  orderNo?: number | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
  rights?: string[] | null;
  options?: RsvpQuestionOption[] | null;
  conditions?: {
    id?: string;
    conditionKind?: string | null;
    operator?: string | null;
    rsvpStatuses?: string[] | null;
    guestGroupId?: string | null;
  }[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface RsvpQuestionInput {
  eventId?: string | null;
  questionType?: string | null;
  valueType?: string | null;
  personField?: string | null;
  title?: string | null;
  helpText?: string | null;
  placeholder?: string | null;
  isRequired?: boolean | null;
  /** WHO must answer: none | all | primary | adults. The server derives isRequired from this. */
  requiredScope?: string | null;
  askOncePerGroup?: boolean | null;
  conditionMatch?: string | null;
  orderNo?: number | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
}

/** Which answers the report counts — mirrors QUESTION_REPORT_AUDIENCE on the server. */
export const QUESTION_REPORT_AUDIENCE = {
  HOLDING_PLACE: 'holding_place',
  EVERYONE: 'everyone',
} as const;

/** Which statistic a question gets — mirrors QUESTION_STAT_KIND on the server, which DERIVES it. */
export const QUESTION_STAT_KIND = {
  NUMBER: 'number',
  CHOICE: 'choice',
  BOOLEAN: 'boolean',
  DATE: 'date',
  TEXT: 'text',
  FILE: 'file',
} as const;

export interface QuestionStatOption {
  rsvpQuestionOptionId?: string | null;
  label?: string | null;
  orderNo?: number | null;
  count?: number | null;
}

export interface QuestionStatText {
  eventGuestId?: string | null;
  guestName?: string | null;
  value?: string | null;
  answeredAt?: string | null;
}

export interface QuestionStat {
  rsvpQuestionId?: string | null;
  title?: string | null;
  questionType?: string | null;
  valueType?: string | null;
  statKind?: string | null;
  personField?: string | null;
  orderNo?: number | null;
  status?: number | null;
  answerCount?: number | null;
  guestCount?: number | null;
  numberMin?: number | null;
  numberMax?: number | null;
  numberAvg?: number | null;
  numberSum?: number | null;
  yesCount?: number | null;
  noCount?: number | null;
  earliestDate?: string | null;
  latestDate?: string | null;
  options?: QuestionStatOption[] | null;
  textAnswers?: QuestionStatText[] | null;
  textTruncated?: boolean | null;
}

export interface EventQuestionStats {
  eventId?: string | null;
  audience?: string | null;
  includePersonFields?: boolean | null;
  audienceGuests?: number | null;
  audienceHeads?: number | null;
  respondedGuests?: number | null;
  totalAnswers?: number | null;
  questionCount?: number | null;
  hiddenPersonFieldQuestions?: number | null;
  questions?: QuestionStat[] | null;
}

// #236 — the answers sheet's types and constants live in a module with no app imports (the guards load it).
export * from "./answerSheetTypes";

/**
 * WHICH FORM a question belongs to — mirrors `QUESTION_ASKED_ON` in the API's `boundary/rsvp.ts`.
 * The house rule is to mirror server constants rather than re-type the strings at call sites.
 */
export const QUESTION_ASKED_ON = {
  RSVP: 'rsvp',
  PARTNER_APPLICATION: 'partner_application',
  // #A4.6 — the volunteer sign-up form.
  VOLUNTEER_APPLICATION: 'volunteer_application',
} as const;

export class RsvpQuestionService {
  /**
   * ⚠ `askedOn` IS A REQUIRED ARGUMENT, not an option with a default.
   *
   * One question engine now backs TWO forms — the RSVP form and the partner-application form — and
   * they share a table. A list that omits the form returns both sets mixed together, so the RSVP
   * editor would show (and let a host reorder and delete) questions that belong to a sponsor form.
   * Making it positional is what lets `tsc` catch a caller that forgets, which the server cannot:
   * its gateway bag is untyped.
   */
  static async list(eventId: string, askedOn: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventId: [eventId], askedOn: [askedOn], status: statuses },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  /** The custom-question report (#42). Both switches have server-side defaults. */
  static async stats(
    eventId: string,
    options?: { audience?: string; includePersonFields?: boolean },
  ): Promise<OpResult> {
    return ApiService.executeQuery(STATS_QUERY, {
      params: {
        eventId,
        audience: options?.audience ?? QUESTION_REPORT_AUDIENCE.HOLDING_PLACE,
        includePersonFields: options?.includePersonFields ?? false,
      },
    });
  }

  /**
   * #236 — the guests' answers sheet. ⚠ THE WHOLE LIST in one read (the server applies no page size): it is printed, and a
   * sheet cut at a cap looks complete in the hand. Refused without Analytics & reports, and for crew without event.operate.
   */
  static async answerSheet(params: AnswerSheetInput): Promise<OpResult> {
    return ApiService.executeQuery(ANSWER_SHEET_QUERY, { params });
  }

  /** #236 — the same sheet as a CSV file `{ fileName, content }`, built on the server from the same input. */
  static async answerSheetCsv(params: AnswerSheetInput): Promise<OpResult> {
    return ApiService.executeQuery(ANSWER_SHEET_CSV_QUERY, { params });
  }

  /**
   * ⚠ `askedOn` is passed SEPARATELY and is required — the server refuses a create without it, and
   * `rsvp_question.asked_on` is NOT NULL with no database default, so there is nothing to fall back
   * to. It is also create-only: moving a question between forms would strand its answers.
   */
  static async create(askedOn: string, params: RsvpQuestionInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params: { ...params, askedOn } });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: RsvpQuestionInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
